import {
	BigNum,
	BN,
	DriftClient,
	MarketType,
	maxSizeForTargetLiabilityWeightBN,
	PerpMarketAccount,
	PRICE_PRECISION_EXP,
	User,
	ZERO,
} from '@drift-labs/sdk';
import {
	UIMarket,
	COMMON_UI_UTILS,
	MARKET_UTILS,
	USER_UTILS,
	TRADING_UTILS,
} from '@drift/common';
import { PredictionMarketConfigs } from 'src/hooks/predictionMarkets/predictionMarketConfigs';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import NumLib from './NumLib';
import { roundBigNumToDecimalPlace } from '@drift/common';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';

export const getSpotLiqPriceNum = (
	user: User,
	marketIndex: number,
	baseSize?: BigNum,
	isLong = true
): number => {
	if (!user || !user.isSubscribed) return 0;

	let baseAssetBN = baseSize ? baseSize.val : undefined;

	if (!isLong && baseAssetBN) {
		baseAssetBN = baseAssetBN.mul(new BN(-1));
	}

	const liqPriceBn = user.spotLiquidationPrice(marketIndex, baseAssetBN);

	const liqPriceNum = BigNum.from(liqPriceBn, PRICE_PRECISION_EXP).toNum();

	return liqPriceNum < 0 ? 0 : liqPriceNum;
};

export const getSpotLiqPriceStr = (
	user: User,
	marketIndex: number,
	baseSize?: BigNum,
	isLong = true
): string => {
	if (!user || !user.isSubscribed) return '';

	const liqPriceNum = getSpotLiqPriceNum(user, marketIndex, baseSize, isLong);

	if (liqPriceNum <= 0) return PRICE_FALLBACK_DISPLAY_VALUE;

	const liqPriceUi = NumLib.formatNum.toDisplayPrice(liqPriceNum);

	return `$${liqPriceUi}`;
};

export const getAccountsInUse = (user: User): number => {
	if (!user || !user.isSubscribed) return 0;
	return (
		user.getActivePerpPositions().length + user.getActiveSpotPositions().length
	);
};

export const baseSizeDisplayForPerpMarket = (
	baseSize: BigNum,
	marketSymbol: string,
	hideValue: boolean,
	decimals?: number
) => {
	const isPredictionmarket =
		PredictionMarketConfigs.isPredictionMarket(marketSymbol);

	if (decimals) {
		baseSize = roundBigNumToDecimalPlace(baseSize, decimals);
	}

	return `${hideValue ? '∗∗∗∗' : baseSize.prettyPrint()} ${
		isPredictionmarket
			? `Shares`
			: COMMON_UI_UTILS.getBaseAssetSymbol(marketSymbol)
	}`;
};

export const getMarginUsedForPosition = TRADING_UTILS.getMarginUsedForPosition;

export const calculateMaxPositionSize = (
	leverage: number,
	marketAccount: PerpMarketAccount | undefined
): BN => {
	if (!marketAccount) return ZERO;

	const targetMarginRatio =
		TRADING_UTILS.convertLeverageToMarginRatio(leverage);

	return (
		maxSizeForTargetLiabilityWeightBN(
			new BN(targetMarginRatio ?? 0),
			new BN(marketAccount.imfFactor),
			new BN(
				marketAccount.highLeverageMarginRatioInitial ??
					marketAccount.marginRatioInitial
			),
			marketAccount
		) ?? ZERO
	);
};

export const getUserMaxLeverageForMarket = (
	marketIndex: number,
	driftClient: DriftClient,
	userClient: User,
	uiMarket: UIMarket
) => {
	const { maxLeverage, highLeverageMaxLeverage, hasHighLeverage } =
		MARKET_UTILS.getMaxLeverageForMarket(
			MarketType.PERP,
			marketIndex,
			driftClient
		);
	const savedMaxLeverage = useMarketSettingsStore
		.getState()
		.getMaxLeverageForMarket(uiMarket.market.symbol);
	const positionMaxLeverage = USER_UTILS.getUserMaxLeverageForMarket(
		userClient,
		marketIndex,
		{
			regularMaxLeverage: maxLeverage,
			highLeverageMaxLeverage,
			hasHighLeverage,
		},
		savedMaxLeverage
	);

	return positionMaxLeverage;
};
